import React from 'react';
import { CheckCircle2, TrendingUp, Network, AlertCircle, Circle } from 'lucide-react';
import { RankingCategory } from '../../../../../../types';

interface RankingCategoryBadgeProps {
  category: RankingCategory;
  size?: 'sm' | 'md';
  showLabel?: boolean;
}

export const RankingCategoryBadge: React.FC<RankingCategoryBadgeProps> = ({
  category,
  size = 'sm',
  showLabel = true
}) => {
  const configs: Record<RankingCategory, {
    icon: React.ElementType;
    label: string;
    className: string;
    tooltip: string;
  }> = {
    CONSENSUS: {
      icon: CheckCircle2,
      label: 'Consensus',
      className: 'bg-green-900/20 text-green-400 border-green-800',
      tooltip: 'Ranks in top 25% for both expression and network priority - highest confidence target'
    },
    TOP_EXPRESSION: {
      icon: TrendingUp,
      label: 'Expression',
      className: 'bg-blue-900/20 text-blue-400 border-blue-800',
      tooltip: 'High differential expression in disease - strong biomarker candidate'
    },
    TOP_NETWORK: {
      icon: Network,
      label: 'Network',
      className: 'bg-purple-900/20 text-purple-400 border-purple-800',
      tooltip: 'High GRN connectivity and therapeutic priority - central regulatory hub'
    },
    DIVERGENT: {
      icon: AlertCircle,
      label: 'Mixed',
      className: 'bg-orange-900/20 text-orange-400 border-orange-800',
      tooltip: 'Large disagreement between ranking methods - interesting for investigation'
    },
    MODERATE: {
      icon: Circle,
      label: 'Moderate',
      className: 'bg-gray-900/20 text-gray-400 border-gray-800',
      tooltip: 'Moderate in both rankings'
    }
  };

  const config = configs[category];
  const Icon = config.icon;
  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1';

  return (
    <span
      className={`inline-flex items-center gap-1 font-medium border rounded-full ${config.className} ${sizeClasses}`}
      title={config.tooltip}
    >
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {showLabel && config.label}
    </span>
  );
};
